import { depositApis } from "@payping/api";
import type { DepositIdentifierDetails } from "@payping/api";
import { dateFormats, isBeforeToday, toUtc } from "@payping/utils/dateUtils";
import {
  ValidCardNumber,
  ValidMobileNumber,
  ValidNationalId,
  ValidPersianName,
  ValidSheba,
} from "@payping/utils/validation";
import {
  Button,
  Col,
  Drawer,
  Flex,
  Input,
  LegacyDatePicker,
  LegacyForm,
  Modal,
  Page,
  Radio,
  Row,
  Space,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import classNames from "classnames";
import { useState } from "react";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { Add, Close, Delete, LightBulb, ShieldWarning } from "@payping/icons";
import { SubLayout } from "@/components/SubLayout";
import styles from "@/assets/scss/Settlement/form.module.scss";
import { useLastUrlWasPage } from "@/utils";
import Success from "./success";
import { SettleIdFormType } from "./types";

const CreateId = () => {
  const { t } = useAppTranslation();
  const [, contextHolder] = Modal.useModal();
  const [form] = LegacyForm.useForm<SettleIdFormType>();
  const [successData, setSuccessData] = useState<DepositIdentifierDetails>();
  const { breakpoints } = usePaypingContext();
  const { lg, sm } = breakpoints;

  const { navigateToLast } = useLastUrlWasPage();

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values: SettleIdFormType) => {
      const date = toUtc({
        convertTime: false,
        date: values.isLegal ? values.registerDate : values.birthDate,
        toFormat: dateFormats.standard.utcDate,
      }).string;
      const data = await depositApis.createDepositIdentifierWithModelPost({
        ...values,
        birthDate: values.isLegal ? undefined : date,
        registerDate: values.isLegal ? date : undefined,
        sourceIbans: (values?.sourceIbans || []).map((item: string) => {
          if (item.startsWith("IR")) {
            return item;
          }
          return `IR${item}`;
        }),
      });
      setSuccessData(data);
    },
    [],
    undefined,
    { useProgress: true },
  );

  const shabaValidation = (value: string) => {
    if (value.includes("IR")) return !ValidSheba(value);
    return !ValidSheba(`IR${value}`);
  };

  // @ts-expect-error TODO:export Rule from LegacyForm.item
  const rulesForIBansAndCardNumber: Rule[] = [
    // @ts-expect-error export Rule from LegacyForm.item
    (formInstance) => {
      const sourceCardNumbers = formInstance.getFieldValue("sourceCardNumbers");
      const sourceIbans = formInstance.getFieldValue("sourceIbans");
      if (
        (sourceIbans && sourceIbans instanceof Array && sourceIbans.length) ||
        (sourceCardNumbers && sourceCardNumbers instanceof Array && sourceCardNumbers.length)
      ) {
        return {};
      }
      return {
        validator: () => Promise.reject(new Error(t("settlement.id.validIbanOrCardNumber"))),
      };
    },
  ];

  return (
    <Drawer
      title={t("settlement.id.create")}
      placement={"bottom"}
      open={true}
      size={"100%"}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigateToLast();
          }}
        ></Button>
      }
    >
      <Page>
        <SubLayout>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={24} lg={14}>
              <Space orientation={"vertical"} size={"large"} className={"tw:w-full"}>
                <Typography.Title level={4}>{t("settlement.id.depositorInformation")}</Typography.Title>
                {!lg ? <Typography.Text type={"secondary"}>{t("settlement.id.createDes")}</Typography.Text> : null}
                <LegacyForm<SettleIdFormType>
                  className={classNames(styles.form)}
                  onFinish={onSubmit}
                  initialValues={{ isLegal: false }}
                  layout="vertical"
                  form={form}
                >
                  <LegacyForm.Item label={t("settlement.id.payer")} rules={[{ required: true }]} name={"isLegal"}>
                    <Radio.Group>
                      <Radio.Button value={false}>{t("natural")}</Radio.Button>
                      <Radio.Button value={true}>{t("legal")}</Radio.Button>
                    </Radio.Group>
                  </LegacyForm.Item>
                  <LegacyForm.Item
                    noStyle
                    className="tw:w-full"
                    shouldUpdate={(prevValues, currentValues) => prevValues.isLegal !== currentValues.isLegal}
                  >
                    {({ getFieldValue }) => {
                      if (getFieldValue("isLegal") === true)
                        return (
                          <>
                            <LegacyForm.Item
                              wrapperCol={{ lg: 10, md: 10 }}
                              label={t("firstName")}
                              name="title"
                              rules={[
                                () => ({
                                  validator(_, value): Promise<void> {
                                    if (value && !ValidPersianName(value)) {
                                      return Promise.reject(new Error(t("settlement.id.validFirstName")));
                                    }
                                    return Promise.resolve();
                                  },
                                }),
                                { required: true },
                              ]}
                            >
                              <Input />
                            </LegacyForm.Item>
                            <LegacyForm.Item
                              wrapperCol={{ lg: 10, md: 10 }}
                              label={t("bNationalCode")}
                              rules={[{ required: true }]}
                              name="businessNationalId"
                            >
                              <Input />
                            </LegacyForm.Item>
                            <LegacyForm.Item
                              wrapperCol={{ lg: 6, md: 6 }}
                              label={t("settings.business.registrationDate")}
                              name="registerDate"
                              rules={[
                                { required: true },
                                {
                                  validator(_, value) {
                                    if (value && !isBeforeToday(value)) {
                                      return Promise.reject(new Error(t("settlement.id.registerDateValidation")));
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <LegacyDatePicker
                                disabledDate={(date) => !isBeforeToday(date)}
                                dateOrder={["YearPicker", "MonthPicker", "DatePicker"]}
                              />
                            </LegacyForm.Item>
                          </>
                        );
                      return (
                        <>
                          <LegacyForm.Item
                            wrapperCol={{ lg: 10, md: 10 }}
                            label={t("firstName")}
                            name="firstName"
                            rules={[
                              () => ({
                                validator(_, value): Promise<void> {
                                  if (value && !ValidPersianName(value)) {
                                    return Promise.reject(new Error(t("settlement.id.validFirstName")));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                              { required: true },
                            ]}
                          >
                            <Input />
                          </LegacyForm.Item>
                          <LegacyForm.Item
                            wrapperCol={{ lg: 10, md: 10 }}
                            label={t("lastName")}
                            name="lastName"
                            rules={[
                              {
                                validator(_, value): Promise<void> {
                                  if (value && !ValidPersianName(value)) {
                                    return Promise.reject(new Error(t("settlement.id.validLastName")));
                                  }
                                  return Promise.resolve();
                                },
                              },
                              { required: true },
                            ]}
                          >
                            <Input />
                          </LegacyForm.Item>
                          <LegacyForm.Item
                            wrapperCol={{ lg: 6, md: 6 }}
                            label={t("birthDate")}
                            name="birthDate"
                            rules={[
                              { required: true },
                              {
                                validator(_, value) {
                                  if (value && !isBeforeToday(value)) {
                                    return Promise.reject(new Error(t("profile.birthDateValidation")));
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <LegacyDatePicker
                              disabledDate={(date) => !isBeforeToday(date)}
                              dateOrder={["YearPicker", "MonthPicker", "DatePicker"]}
                            />
                          </LegacyForm.Item>
                          <LegacyForm.Item
                            wrapperCol={{ lg: 6, md: 6 }}
                            label={t("nationalCode")}
                            name="nationalCode"
                            rules={[
                              () => ({
                                validator(_, value): Promise<void> {
                                  if (value && !ValidNationalId(value)) {
                                    return Promise.reject(new Error(t("settlement.id.validBNationalCode")));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                              { message: t("error.requiredForTaxCode", { name: t("nationalCode") }), required: true },
                            ]}
                          >
                            <Input />
                          </LegacyForm.Item>
                          <LegacyForm.Item
                            wrapperCol={{ lg: 6, md: 6 }}
                            label={t("mobile")}
                            rules={[
                              () => ({
                                validator(_, value) {
                                  if (value && !ValidMobileNumber(value)) {
                                    return Promise.reject(new Error(t("settlement.id.validPhoneNumber")));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                              { required: true },
                            ]}
                            name={"phoneNumber"}
                          >
                            <Input maxLength={11} />
                          </LegacyForm.Item>
                        </>
                      );
                    }}
                  </LegacyForm.Item>
                  <LegacyForm.Item wrapperCol={{ lg: 8, md: 8 }} label={t("description")} name={"description"}>
                    <Input.TextArea className={"tw:w-full"} />
                  </LegacyForm.Item>
                  <Space orientation={"vertical"} className={"tw:w-full"} size={"small"}>
                    <Typography.Text>{t("settlement.id.shabaNumberList")}</Typography.Text>
                    <Typography.Text type={"secondary"} strong>
                      {
                        "لورم ایپسوم متن ساختگی با تولید سادگی نامفهوم از صنعت چاپ و با استفاده از طراحان گرافیک است چاپگرها و متون بلک"
                      }
                    </Typography.Text>

                    <LegacyForm.List name="sourceIbans">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <LegacyForm.Item
                              wrapperCol={{ lg: 10, md: 10 }}
                              className="tw:w-full"
                              key={key}
                              label={t("settlement.id.iban")}
                            >
                              <Flex gap={"small"} vertical={!lg} key={key}>
                                <LegacyForm.Item
                                  {...restField}
                                  noStyle
                                  name={[name]}
                                  rules={[
                                    { required: true, message: t("settlement.id.validIban") },
                                    () => ({
                                      validator(_, value) {
                                        if (value && shabaValidation(value)) {
                                          return Promise.reject(new Error(t("settlement.id.validIban")));
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <Input
                                    placeholder={t("settlement.create.shebaInput")}
                                    addonAfter={"IR"}
                                    onPressEnter={(event) => {
                                      event.preventDefault();
                                    }}
                                  />
                                </LegacyForm.Item>

                                <Button onClick={() => remove(name)} icon={<Delete width={14} height={14} />}>
                                  {t("settlement.id.removeFromList")}
                                </Button>
                              </Flex>
                            </LegacyForm.Item>
                          ))}
                          <LegacyForm.Item name={"addIbans"} rules={rulesForIBansAndCardNumber}>
                            <Button
                              onClick={() => add()}
                              icon={<Add width={14} height={14} />}
                              className={sm ? "mt-0" : "mt-2"}
                            >
                              {t("settlement.id.addIban")}
                            </Button>
                          </LegacyForm.Item>
                        </>
                      )}
                    </LegacyForm.List>

                    <LegacyForm.List name="sourceCardNumbers">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <LegacyForm.Item
                              wrapperCol={{ lg: 10, md: 10 }}
                              className="tw:w-full"
                              key={key}
                              label={t("settlement.id.cardNumber")}
                            >
                              <Flex gap={"small"} vertical={!lg} key={key}>
                                <LegacyForm.Item
                                  {...restField}
                                  noStyle
                                  name={[name]}
                                  rules={[
                                    { required: true, message: t("settlement.id.validCardNumber") },
                                    () => ({
                                      validator(_, value) {
                                        if (value && !ValidCardNumber(value)) {
                                          return Promise.reject(new Error(t("settlement.id.validCardNumber")));
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <Input
                                    onPressEnter={(event) => {
                                      event.preventDefault();
                                    }}
                                  />
                                </LegacyForm.Item>

                                <Button onClick={() => remove(name)} icon={<Delete width={14} height={14} />}>
                                  {t("settlement.id.removeFromList")}
                                </Button>
                              </Flex>
                            </LegacyForm.Item>
                          ))}
                          <LegacyForm.Item name={"addcardNumber"} rules={rulesForIBansAndCardNumber}>
                            <Button
                              onClick={() => add()}
                              icon={<Add width={14} height={14} />}
                              className={sm ? "mt-0" : "mt-2"}
                            >
                              {t("settlement.id.addcardNumber")}
                            </Button>
                          </LegacyForm.Item>
                        </>
                      )}
                    </LegacyForm.List>
                  </Space>
                  <Row gutter={8}>
                    <Col xs={16} xl={8}>
                      <Button
                        block
                        type={"primary"}
                        htmlType={"submit"}
                        loading={loadingSubmit}
                        disabled={loadingSubmit}
                      >
                        {t("save")}
                      </Button>
                    </Col>
                    <Col xs={8} xl={4}>
                      <Button block onClick={() => navigateToLast()}>
                        {t("cancel")}
                      </Button>
                    </Col>
                  </Row>
                  {contextHolder}
                </LegacyForm>
                <Success data={successData} setData={setSuccessData} />
              </Space>
            </Col>
            {lg ? (
              <Col xs={24} lg={10}>
                <Space
                  orientation={"vertical"}
                  className="background--gray100 tw:sticky tw:top-0 tw:w-full tw:p-16!"
                  size={"large"}
                >
                  <Space orientation={"vertical"} className={"tw:align-center tw:flex tw:w-full"} size={"middle"}>
                    <LightBulb width={60} />
                    <Typography.Title level={5}>{t("settlement.id.helpId")}</Typography.Title>
                  </Space>
                  <Flex vertical={false} gap={"middle"}>
                    <ShieldWarning width={14} />
                    <Typography.Text type={"secondary"}>{t("settlement.id.attention0")}</Typography.Text>
                  </Flex>
                  <Flex vertical={false} gap={"middle"}>
                    <ShieldWarning width={14} />
                    <Typography.Text type={"secondary"}>{t("settlement.id.attention1")}</Typography.Text>
                  </Flex>
                  <Flex vertical={false} gap={"middle"}>
                    <ShieldWarning width={14} />
                    <Typography.Text type={"secondary"}>{t("settlement.id.attention2")}</Typography.Text>
                  </Flex>
                  <Flex vertical={false} gap={"middle"}>
                    <ShieldWarning width={14} />
                    <Typography.Text type={"secondary"}>{t("settlement.id.attention3")}</Typography.Text>
                  </Flex>
                  <Flex vertical={false} gap={"middle"}>
                    <ShieldWarning width={14} />
                    <Typography.Text type={"secondary"}>{t("settlement.id.attention4")}</Typography.Text>
                  </Flex>
                  <Flex vertical={false} gap={"middle"}>
                    <ShieldWarning width={14} />
                    <Typography.Text type={"secondary"}>{t("settlement.id.attention5")}</Typography.Text>
                  </Flex>
                </Space>
              </Col>
            ) : null}
          </Row>
        </SubLayout>
      </Page>
    </Drawer>
  );
};

export default CreateId;
